import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import { isAiXstateMigrationEnabled } from '@atlaskit/editor-common/ai-xstate-migration';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { A2uiSurfaceAdapterRovoMessage } from '@atlassian/a2ui-renderer-next/v0_9/a2ui-surface-adapter-rovo';
import type { Metric } from '@atlassian/browser-metrics/metric';
import {
	ExperienceName,
	sendMessageExperienceName,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	isConversationChannelDataMessage,
	isFollowUpMessage,
	isScopeDeterminedMessage,
	isSpaceSourceRecommendationsMessage,
	isSSEMessage,
	isStreamActionClassification,
	isStreamActionComplete,
	isStreamActionError,
	isStreamActionPart,
	isStreamActionPrepare,
	isStreamAgenticUi,
	isStreamAnswerPart,
	isStreamAttachmentBlock,
	isStreamAUPError,
	isStreamError,
	isStreamEntityAnnotations,
	normalizeEntityAnnotationsMessage,
	isStreamConciergeAgentSelected,
	isStreamFastModeEnabled,
	isStreamFastModeResponseAvailable,
	isStreamMemoryUpdate,
	isStreamPromptBlock,
	isStreamPromptRedaction,
	isStreamReasoningPart,
	isStreamReconnectSupportPart,
	isStreamRemoveAnswerNow,
	isStreamResearchPayload,
	isStreamResponse,
	isStreamResponseBlock,
	isStreamResponseRedaction,
	isStreamTaskStatus,
	isStreamThinkDeeperTriggered,
	isStreamTodoSnapshot,
	isStreamTrace,
	type StreamAgenticUi,
	type StreamMessage,
	type StreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	endActiveAgentRun,
	notifyAgentRunStateForConversation,
} from '@atlassian/conversation-assistant-store/controllers/agent-mention-run-state';
import { deriveFollowUpSendAgentIdentity } from '@atlassian/conversation-assistant-store/controllers/chat/derive-follow-up-send-agent-identity';
import {
	AnswerNowStatusType,
	CHAT_STORE_STATUS,
	isStoreAssistant,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { isDefaultRovoChat } from '@atlassian/conversation-assistant-utils/default-agent';
import {
	isPeopleEntityAnnotationEnabled,
	isInferredProjectEntityAnnotationEnabled,
} from '@atlassian/conversation-assistant-utils/isEntityAnnotationExperimentEnabled';
import { RovoChatStreamTimeoutError } from '@atlassian/conversation-assistant-utils/RovoChatStreamTimeoutError';
import { withAsyncGeneratorTimeout } from '@atlassian/conversation-assistant-utils/withAsyncGeneratorTimeout';

import { abortGenerateContentChildExperiences } from '../../common/utils/aifc-experience-collect/abort-generate-content-child-experiences';
import { completeAifcActionExperience } from '../../common/utils/aifc-experience-collect/complete-aifc-action-experience';
import { messageLatencyMetrics } from '../../common/utils/browser-metrics/messageLatencyMetrics';
import { countFragmentWords } from '../../common/utils/content-features/countFragmentWords';
import { getTopLevelNodeTypes } from '../../common/utils/content-features/getTopLevelNodeTypes';
import { randomIntFromInterval } from '../../common/utils/id-generator';
import { isAnswerUiPartsEnabled } from '../../common/utils/is-answer-ui-parts-enabled';
import { StreamLifecycleState } from '../bridge-api/streaming-lifecycle/StreamLifecycleState';
import { slidesAiEditLatency } from '../slides-ai-edit-latency';
import { addAnswerPart } from './addAnswerPart';
import { addAnswerPartWithMarkdownDataMap } from './addAnswerPartWithMarkdownDataMap';
import { addMemorytoLatestAssistantMessage } from './addMemorytoLatestAssistantMessage';
import { addRequestIdToLatestHumanMessage } from './addRequestIdToLatestHumanMessage';
import { createAnswerUiPartsStreamHandler } from './answer-ui-parts-stream-handler';
import { appendCreatePageFollowUpIfNeeded } from './appendCreatePageFollowUpIfNeeded';
import { appendEntityAnnotationBatch } from './appendEntityAnnotationBatch';
import { applyResponseRedactionToMessage } from './applyResponseRedactionToMessage';
import { clearAnswerNowStatusForMessageIfSet } from './clearAnswerNowStatusForMessageIfSet';
import { clearTracesIfNotEmpty } from './clearTracesIfNotEmpty';
import { ACTION_EXECUTION_FAILED } from './constants';
import { createBetweenChunksInactivityLogger } from './createBetweenChunksInactivityLogger';
import { createChatReconnectableStream } from './createChatReconnectableStream';
import { createStreamMarkdownMultiTagProcessor } from './createStreamMarkdownMultiTagProcessor';
import { createSyntheticCreatePageFollowUp } from './createSyntheticCreatePageFollowUp';
import { createDeadAirTracker } from './dead-air-tracker';
import { dedupeTraceMessagesWithLatest } from './dedupeTraceMessages';
import { addEntityAnnotationBatchCounts } from './entityAnnotationAnalytics';
import { StreamErrorException } from './error-classes/StreamErrorException';
import { StreamGeneratorError } from './error-classes/StreamGeneratorError';
import { extractStreamMeta } from './extractStreamMeta';
import { findAddressableSurfaces } from './findAddressableSurfaces';
import { findStoreMessageIndex } from './findStoreMessageIndex';
import { getActionPartKeyForMetrics } from './getActionPartKeyForMetrics';
import { getConversation } from './getConversation';
import { getConversationTraces } from './getConversationTraces';
import { getInteractionModeExecutedFromStreamResponse } from './getInteractionModeExecutedFromStreamResponse';
import { getInvokedFromInteractionSource } from './getInvokedFromInteractionSource';
import { getLastAgentMessageID } from './getLastAgentMessageID';
import { getRemixRegenerateRequestAttributes } from './getRemixRegenerateRequestAttributes';
import { getRequestedModeAttributes } from './getRequestedModeAttributes';
import { getSourceAgentId } from './getSourceAgentId';
import { getTraceFromStreamMessage } from './getTraceFromStreamMessage';
import { handleStagingAreaActionValidation } from './handleStagingAreaActionValidation';
import { invalidatesProcessedFinalResponse } from './invalidatesProcessedFinalResponse';
import { isClientSideStreamingError } from './isClientSideStreamingError';
import { isFinalResponseAwaitingConfirmation } from './isFinalResponseAwaitingConfirmation';
import { minionVerificationAnalytics } from './minion-verification-analytics';
import { processAifcActionStreamMessage } from './process-aifc-action-stream-message/process-aifc-action-stream-message';
import { processAutoActionApprovalStreamMessage } from './processAutoActionApprovalStreamMessage';
import { processConversationTitleUpdated } from './processConversationTitleUpdated';
import { processInteractionConclusion } from './processInteractionConclusion';
import { processResearchStreamMessage } from './processResearchStreamMessage';
import { processStreamResponseMarkdown } from './processStreamResponseMarkdown';
import { recordStreamMessage } from './recordStreamMessage';
import { replaceAnswerData } from './replaceAnswerData';
import { resetStreamResponseRecording } from './resetStreamResponseRecording';
import { resolveConciergeSelectedAgentUpdate } from './resolveConciergeSelectedAgentUpdate';
import { routeA2UIMessages } from './routeA2UIMessages';
import type { ProcessStreamParams } from './sendMessageActionTypes';
import { shouldAppendCreatePageFollowUp } from './shouldAppendCreatePageFollowUp';
import { shouldPreprocessDetailsForConversation } from './shouldPreprocessDetailsForConversation';
import type { ResearchStreamState } from './types';
import { updateHumanMessageId } from './updateHumanMessageId';
import { updateStoreAssistantMessageAnswerNowStatus } from './updateStoreAssistantMessageAnswerNowStatus';
import { updateStoreAssistantMessageId } from './updateStoreAssistantMessageId';

type InternalStreamStats = {
	totalChunks: number;
	traceChunks: number;
	answerChunks: number;
	actionChunks: number;
	researchChunks: number;
	otherChunks: number;
	firstChunkAt?: number;
	lastChunkAt?: number;
	sumInterChunkMs: number;
	pluginsObserved: Set<string>;
};

const getStreamStats = () => {
	const stats: InternalStreamStats = {
		totalChunks: 0,
		traceChunks: 0,
		answerChunks: 0,
		actionChunks: 0,
		researchChunks: 0,
		otherChunks: 0,
		firstChunkAt: undefined,
		lastChunkAt: undefined,
		sumInterChunkMs: 0,
		pluginsObserved: new Set<string>(),
	};

	const update = (streamMessage: StreamMessage) => {
		const now = performance.now();
		stats.totalChunks += 1;
		if (stats.firstChunkAt === undefined) {
			stats.firstChunkAt = now;
		}
		if (stats.lastChunkAt !== undefined) {
			stats.sumInterChunkMs += now - stats.lastChunkAt;
		}
		stats.lastChunkAt = now;

		if (isStreamTrace(streamMessage)) {
			stats.traceChunks += 1;
		} else if (isStreamAnswerPart(streamMessage)) {
			stats.answerChunks += 1;
		} else if (isStreamResearchPayload(streamMessage)) {
			stats.researchChunks += 1;
		} else if (isStreamActionPart(streamMessage) || isStreamActionPrepare(streamMessage)) {
			// Count action-related chunks (prepare/part)
			stats.actionChunks += 1;
		} else {
			// Count unknown chunks
			stats.otherChunks += 1;
		}

		// Observe plugin names if present early (often only present on final response)
		const invocations = isStreamResponse(streamMessage)
			? streamMessage.message?.message?.plugin_invocations
			: [];
		if (Array.isArray(invocations)) {
			invocations.forEach((p) => p?.plugin_name && stats.pluginsObserved.add(p.plugin_name));
		}
	};

	const getStats = () => {
		const avgGapMs =
			stats.totalChunks > 1 ? Math.round(stats.sumInterChunkMs / (stats.totalChunks - 1)) : 0;
		return {
			...stats,
			pluginsObserved: Array.from(stats.pluginsObserved),
			averageGapMs: avgGapMs,
		};
	};

	return { update, getStats };
};

const ROVO_CHAT_STREAM_IDLE_TIMEOUT_MS = 120_000;
const NO_FINAL_RESPONSE_ERROR_MESSAGE = 'The response took too long and timed out';

export const processStream = async ({
	prompt,
	generator,
	productKey,
	answerIndex,
	tmpHumanMessageId,
	tmpAssistantMessageId,
	thinkDeeperEnabled,
	fastModeEnabled,
	deepResearchEnabled,
	webSearchEnabled,
	sendMessageExperienceNameCompiled,
	streamTracking,
	traceId,
	updateCurrentConversation,
	trackingAttributes,
	editRequestId,
	conversationId,
	getState,
	promptStreamHook,
	openChat,
	openChatOnError,
	experienceTracker,
	setMessages,
	getMessages,
	onSpaceSourceRecommendations,
	processStreamResearch,
	processMessageStreamAction,
	processMessageStreamComplete,
	processMessageStreamError,
	processMessageStreamActionClassification,
	containerOnTempMessageIdReplaced,
	containerOnHumanMessageIdReplaced,
	updateResearchMessageId,
	sendAnalyticsEvent,
	chatContextStoreActions,
	stopInteractionMetricsTimeToFirstActionPart,
	stopInteractionMetricsTimeToFirstAnswerPart,
	stopInteractionMetricsTimeToActionCompletion,
	broadcastStreamLifecycle,
	sendMessageLatencyMetrics,
	cancelStreamingMetrics,
	onTempMessageIdReplacedOverride,
	streamActionInfo,
	trackAIMAUEvent,
	messageSources,
	additionalAnalyticAttributes,
	unsupportedRemixNodeTypes,
	hasLoggedInitiatedEvent,
	clearMessageResearch,
	addImplicitResearchPlan,
	resetHasReceivedAnswerPart,
	viewDeadAirBroker,
	minionAlias,
	requestedSkills,
	agentId,
	agentName,
	agentIdentityAccountId,
	agentNamedId,
	isThirdPartyAgent,
	creationContextParams,
	supportsRemixTextResponse = false,
	storeUserMessages,
	skillSelectionSource,
	assistanceService,
	abortController,
	getIsChatMounted,
	metricsStartTime,
	onTaskStatusUpdate,
	onTodoSnapshot,
	onPromptRedacted,
	onPromptBlocked,
	onResponseRedacted,
	onResponseBlocked,
	onAttachmentBlocked,
	recoverConversationAfterStreamTimeout,
}: ProcessStreamParams): Promise<
	| {
			hasReceivedFinalResponse: boolean;
			hasReceivedPromptRedaction: boolean;
			hasLoggedInitiatedEvent: boolean;
			guardEventReceived: boolean;
			assistantMessageId: string;
			recoveredAfterStreamTimeout: boolean;
			hasAbortedForImplicitThinkDeeper?: undefined;
			implicitThinkDeeperSendMessageMetrics?: undefined;
			implicitThinkDeeperExperienceName?: undefined;
	  }
	| {
			hasReceivedFinalResponse: boolean;
			hasReceivedPromptRedaction: boolean;
			hasLoggedInitiatedEvent: boolean;
			guardEventReceived: boolean;
			assistantMessageId: string;
			recoveredAfterStreamTimeout: boolean;
			hasAbortedForImplicitThinkDeeper: boolean;
			implicitThinkDeeperSendMessageMetrics: Metric | null;
			implicitThinkDeeperExperienceName: string | null;
	  }
> => {
	updateCurrentConversation({
		traces: [],
	});
	const remixRegenerateRequestAttributes = getRemixRegenerateRequestAttributes({
		creationContextParams,
		messageSources,
	});

	// Set to true when a guard event (PROMPT_REDACTION or PROMPT_BLOCKED) is received.
	// The backend always follows these events with an ERROR event to terminate the stream,
	// which should be treated as a successful end of the guard flow, not a real error.
	let guardEventReceived = false;

	const acknowledgedInlineEdits = new Set<string>();
	let hasReceivedFinalResponse = false;
	let hasProcessedFinalResponse = false;
	let lastInvalidatingEventType: string | undefined;
	let recoveredAfterStreamTimeout = false;
	let hasReceivedPromptRedaction = false;
	let hasReceivedFollowUp = false;
	let turnSurfaces: Map<string, string> | undefined;
	let actionCompleteCount = 0;
	let actionClassificationCount = 0;
	let isBackendCreationRequest = false;
	let agentMessageId: string | undefined;
	let isReconnectSupported = false;
	// Guards against repeated metric switches if multiple REASONING_PART messages arrive.
	let hasAbortedForImplicitThinkDeeper = false;
	// Think-deeper metrics started when implicit think-deeper is detected mid-stream. Null otherwise.
	let implicitThinkDeeperSendMessageMetrics: ReturnType<
		typeof messageLatencyMetrics.createSendMessageMetrics
	> | null = null;
	let implicitThinkDeeperTimeToFirstAnswerPartMetrics: ReturnType<
		typeof messageLatencyMetrics.createTimeToFirstAnswerPartMetrics
	> | null = null;
	// The experience name for the reliability SLO when implicit think-deeper is detected. Null otherwise.
	let implicitThinkDeeperExperienceName: string | null = null;

	// Client-side implicit round tracking for think deeper.
	// Passed by reference into processResearchStreamMessage so state is shared.
	const researchState: ResearchStreamState = {
		clientRoundCounter: 0,
		hasAccumulatedAnswerContent: false,
	};

	// Gate the details-tag preprocessor to 3P-agent conversations only.
	// Lazily initialised on the first chunk: the conversation's agent metadata
	// is set by `createConversationOptimistically` which may run after this
	// function is defined but before the first chunk arrives.
	let processStreamMarkdownMultiTagInstance: ReturnType<
		typeof createStreamMarkdownMultiTagProcessor
	> | null = null;
	const getShouldPreprocessDetails = () =>
		shouldPreprocessDetailsForConversation(getState(), conversationId);
	const getProcessStreamMarkdownMultiTag = () => {
		if (!processStreamMarkdownMultiTagInstance) {
			processStreamMarkdownMultiTagInstance = createStreamMarkdownMultiTagProcessor({
				shouldPreprocessDetails: getShouldPreprocessDetails(),
			});
		}
		return processStreamMarkdownMultiTagInstance;
	};

	const onAnswerActivity = ({
		answerType,
		hasContent,
	}: {
		answerType: 'stream-answer-part' | 'stream-answer-ui-part';
		hasContent: boolean;
	}) => {
		stopInteractionMetricsTimeToFirstAnswerPart('stop', {
			answerType,
			conversationId,
		});
		// Stop the implicit think-deeper time-to-first-answer-part metric if active
		implicitThinkDeeperTimeToFirstAnswerPartMetrics?.stop({
			customData: { answerType, conversationId },
		});

		// Clear Answer Now status when the answer starts
		// This handles the case where the stream starts answering without user clicking Answer Now
		clearAnswerNowStatusForMessageIfSet(
			conversationId,
			tmpAssistantMessageId,
			getMessages,
			setMessages,
		);

		// Sometimes an ANSWER_PART arrives with empty content, which clears traces
		// prematurely and makes the live "thinking" steps flash and disappear. Skip the
		// clear for empty answer parts so traces persist until real content arrives.
		if (hasContent) {
			clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);
			// Mark hasReceivedAnswerPart on the chat store message atomically with the
			// trace clear. This avoids a cross-store render gap (chat store vs research
			// store) where the thinking indicator would briefly re-appear.
			setMessages(
				conversationId,
				(getMessages(conversationId) ?? []).map((msg) => {
					if (
						msg.type === 'ASSISTANT' &&
						msg.content.message?.message?.id === tmpAssistantMessageId
					) {
						return { ...msg, hasReceivedAnswerPart: true };
					}
					return msg;
				}),
			);
		}
	};

	const answerUiParts = isAnswerUiPartsEnabled()
		? createAnswerUiPartsStreamHandler({
				conversationId,
				getAnswerId: () => tmpAssistantMessageId,
				answerIndex,
				getMessages,
				setMessages,
				onUiPartPlaced: () =>
					onAnswerActivity({ answerType: 'stream-answer-ui-part', hasContent: true }),
			})
		: null;

	let latestStreamMessage: StreamMessage | undefined;

	const streamStats = getStreamStats();
	const peopleEntityHoverCardsEnabled = isPeopleEntityAnnotationEnabled();
	const inferredProjectHoverCardsEnabled = isInferredProjectEntityAnnotationEnabled();
	const entityAnnotationAnalyticsEnabled =
		peopleEntityHoverCardsEnabled || inferredProjectHoverCardsEnabled;
	let entityAnnotationCounts = {
		inferredProjectEntityAnnotationCount: 0,
		personEntityAnnotationCount: 0,
	};
	// Between-chunks inactivity watchdog (logs once per stall, re-arms on chunks)
	const inactivity = createBetweenChunksInactivityLogger({
		baseMs: 90_000,
		sendAnalyticsEvent: (event: AnalyticsEventPayload) => {
			sendAnalyticsEvent({
				...event,
				attributes: {
					...event.attributes,
					...streamStats.getStats(),
					isDeepResearchEnabled: deepResearchEnabled,
					isThinkDeeperEnabled: thinkDeeperEnabled,
					isFastModeEnabled: fastModeEnabled,
					isWebSearchEnabled: webSearchEnabled,
					isAIFCStreaminAction: streamActionInfo.isAction,
					conversationId,
					instrumentationId: trackingAttributes.instrumentationId,
					requestId: trackingAttributes.requestId,
					interactionSource: trackingAttributes.interactionSource,
					contentContext: chatContextStoreActions.getIsStagingArea() ? 'staging-area' : 'global',
					userAgent: navigator?.userAgent,
				},
			});
		},
	});

	// Dead air tracking - monitors gaps between meaningful stream messages
	const deadAirTracker = createDeadAirTracker({
		messageId: tmpAssistantMessageId,
		conversationId,
		traceId: traceId ?? '',
		productKey: productKey ?? '',
		deepResearchEnabled: deepResearchEnabled ?? false,
		thinkDeeperEnabled: thinkDeeperEnabled ?? false,
		fastModeEnabled: fastModeEnabled ?? false,
		webSearchEnabled: webSearchEnabled ?? false,
		isAIFC: streamActionInfo.isAction,
		sendAnalyticsEvent,
	});

	viewDeadAirBroker?.start({
		messageId: tmpAssistantMessageId,
		conversationId,
		traceId: traceId ?? '',
		productKey: productKey ?? '',
		reasoningMode: deepResearchEnabled
			? 'deep-research'
			: thinkDeeperEnabled
				? 'think-deeper'
				: fastModeEnabled
					? 'fast'
					: 'auto',
		isAIFCEnabled: streamActionInfo.isAction,
		isWebSearchEnabled: webSearchEnabled ?? false,
	});

	// Reset the stream response recording for a new stream session.
	// Recording is always enabled so that network responses can be captured by default.
	const canRecordStreamResponses = true;

	if (canRecordStreamResponses) {
		resetStreamResponseRecording();
	}

	// Arm the inactivity watchdog
	inactivity?.arm();

	/*****************************
	 *
	 * Stream processing START
	 *
	 *****************************/

	// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- Preserve the existing timeout experiment while keeping this reliability change scoped.
	const isStreamTimeoutEnabled = FeatureGates.getExperimentValue(
		'rovo_chat_hung_stream_timeout',
		'isEnabled',
		false,
	);
	// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- Preserve the existing timeout experiment while keeping this reliability change scoped.
	const streamIdleTimeoutMs = FeatureGates.getExperimentValue<number>(
		'rovo_chat_hung_stream_timeout',
		'timeoutMs',
		ROVO_CHAT_STREAM_IDLE_TIMEOUT_MS,
	);
	const isStreamTimeoutRecoveryEnabled =
		isStreamTimeoutEnabled && isExperimentEnabled('rovo_chat_stream_timeout_recovery');
	let lastStreamTimeoutError: RovoChatStreamTimeoutError | undefined;
	const recoverAfterStreamTimeout = async (error: unknown): Promise<boolean> => {
		const recovered = (await recoverConversationAfterStreamTimeout?.(error)) ?? false;

		if (!recovered) {
			slidesAiEditLatency.markFailed(editRequestId);
			abortController.abort();
			return false;
		}

		hasReceivedFinalResponse = true;
		recoveredAfterStreamTimeout = true;
		sendAnalyticsEvent({
			eventType: 'track',
			action: 'recovered',
			actionSubject: 'rovoChatStream',
			attributes: {
				conversationChannelId: conversationId,
				experienceName: sendMessageExperienceNameCompiled,
				recoveryMethod: 'conversationRefresh',
				requestId: trackingAttributes.requestId,
				traceId,
			},
		});
		return true;
	};

	const onStreamTimeout = () => {
		if (hasReceivedFinalResponse || guardEventReceived || abortController.signal.aborted) {
			return undefined;
		}

		const timeoutError = new RovoChatStreamTimeoutError(NO_FINAL_RESPONSE_ERROR_MESSAGE, {
			requestId: trackingAttributes.requestId,
			traceId,
		});
		if (isStreamTimeoutRecoveryEnabled) {
			lastStreamTimeoutError = timeoutError;
		} else {
			streamTracking?.fail(new Error(NO_FINAL_RESPONSE_ERROR_MESSAGE));
			slidesAiEditLatency.markFailed(editRequestId);
			abortController.abort();
		}
		sendAnalyticsEvent({
			eventType: 'track',
			action: 'timedOut',
			actionSubject: 'rovoChatStream',
			attributes: {
				conversationChannelId: conversationId,
				experienceName: sendMessageExperienceNameCompiled,
				requestId: trackingAttributes.requestId,
				timeoutMs: streamIdleTimeoutMs,
				timeoutType: 'idle',
				traceId,
			},
		});
		return timeoutError;
	};

	const reconnectableStream = createChatReconnectableStream({
		generator,
		conversationId,
		abortController,
		assistanceService,
		sendAnalyticsEvent,
		experienceTracker,
		getMessageId: () => agentMessageId,
		canReconnect: () => !!(agentMessageId && isReconnectSupported),
		hasProcessedFinalResponse: () => hasProcessedFinalResponse,
		getInvalidatedFinalResponse: () =>
			hasReceivedFinalResponse && !hasProcessedFinalResponse
				? lastInvalidatingEventType
				: undefined,
		getState,
		traceId,
		updateCurrentConversation,
		getIsChatMounted,
		analyticsAttributes: {
			isDeepResearch: deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
		},
		onBeforeFirstReconnect: () => {
			const chat = getMessages(conversationId);
			const alreadySwapped = findStoreMessageIndex(chat, agentMessageId!) >= 0;

			if (!alreadySwapped) {
				setMessages(
					conversationId,
					updateStoreAssistantMessageId({
						chat,
						oldId: tmpAssistantMessageId,
						newId: agentMessageId!,
					}),
				);
				if (tmpAssistantMessageId !== agentMessageId!) {
					updateResearchMessageId({
						messageId: tmpAssistantMessageId,
						newMessageId: agentMessageId!,
					});
				}
			}

			tmpAssistantMessageId = agentMessageId!;
		},
		onReconnected: () => {
			inactivity?.arm();
		},
		onFirstChunkAfterReconnect: () => {
			deadAirTracker?.resetTimestamp();
		},
		streamTimeout:
			isStreamTimeoutEnabled && isStreamTimeoutRecoveryEnabled
				? {
						timeoutMs: streamIdleTimeoutMs,
						onTimeout: onStreamTimeout,
						onReconnectFailure: recoverAfterStreamTimeout,
					}
				: undefined,
	});
	const streamToIterate =
		isStreamTimeoutEnabled && !isStreamTimeoutRecoveryEnabled
			? withAsyncGeneratorTimeout(reconnectableStream, {
					timeoutMs: streamIdleTimeoutMs,
					// Preserve the existing timeout experiment behavior outside the recovery treatment.
					isPaused: () => typeof navigator !== 'undefined' && !navigator.onLine,
					onTimeout: onStreamTimeout,
				})
			: reconnectableStream;

	let canCompleteRemixWithText = supportsRemixTextResponse;
	let isValidRemixTextResponse = false;
	let isCurrentRemixRegenerationStream =
		remixRegenerateRequestAttributes.isRemixRegenerate === true;
	// A superseded stream must not settle the next regeneration
	const unsubscribeRemixTrackers =
		canCompleteRemixWithText || isCurrentRemixRegenerationStream
			? experienceTracker.subscribe((event) => {
					if (event.name === ExperienceName.REMIX_REGENERATE && event.action === 'taskStart') {
						canCompleteRemixWithText = false;
						isCurrentRemixRegenerationStream = false;
					}
				})
			: undefined;

	try {
		for await (const streamObj of streamToIterate) {
			const streamMessage = isSSEMessage(streamObj) ? streamObj.data : streamObj;
			if (invalidatesProcessedFinalResponse(streamObj, agentMessageId)) {
				hasProcessedFinalResponse = false;
				lastInvalidatingEventType =
					(isSSEMessage(streamObj) ? streamObj.eventType : undefined) ??
					streamMessage.type ??
					'UNKNOWN';
			}
			if (
				isStreamActionClassification(streamMessage) ||
				isStreamActionPrepare(streamMessage) ||
				isStreamActionPart(streamMessage) ||
				isStreamActionComplete(streamMessage) ||
				isStreamActionError(streamMessage) ||
				isStreamError(streamMessage)
			) {
				canCompleteRemixWithText = false;
			}

			// Record the raw stream message for debugging purposes
			// This allows developers to copy/paste SSE responses for examples/tests
			if (canRecordStreamResponses) {
				recordStreamMessage(streamMessage);
			}

			latestStreamMessage = streamMessage;
			deadAirTracker?.update(streamMessage);
			streamStats.update(streamMessage);
			// any chunk = proof of life
			inactivity?.onChunk();

			// Note: We do NOT skip stream messages during SWITCHING.
			// Stream processing continues normally until FAST_MODE_ENABLED is received,
			// at which point research steps are cleared and status is set to SWITCHED.

			if (
				processConversationTitleUpdated(streamMessage, conversationId, updateCurrentConversation)
			) {
				continue;
			}

			// Usually the first stream part
			if (isConversationChannelDataMessage(streamMessage)) {
				agentMessageId = streamMessage.assistant_message_id;
				const streamConversationName = streamMessage.name?.trim();

				updateCurrentConversation({
					...(streamConversationName ? { name: streamConversationName } : {}),
					lastMessageTimestamp: streamMessage.last_message_timestamp ?? new Date().toISOString(),
					timeCreated: streamMessage.time_created,
					...(streamMessage.scopes && { scopes: streamMessage.scopes }),
				});

				trackingAttributes.instrumentationId = agentMessageId;
				trackingAttributes.messageId = agentMessageId;

				if (agentMessageId && agentMessageId !== tmpAssistantMessageId) {
					const oldTmpId = tmpAssistantMessageId;
					setMessages(
						conversationId,
						updateStoreAssistantMessageId({
							chat: getMessages(conversationId),
							oldId: oldTmpId,
							newId: agentMessageId,
						}),
					);
					updateResearchMessageId({
						messageId: oldTmpId,
						newMessageId: agentMessageId,
					});
					tmpAssistantMessageId = agentMessageId;
					(onTempMessageIdReplacedOverride || containerOnTempMessageIdReplaced)?.(
						oldTmpId,
						agentMessageId,
					);
				}

				trackingAttributes.invokedFrom =
					creationContextParams?.source === 'remix'
						? 'remix'
						: getInvokedFromInteractionSource(messageSources?.interactionSource);

				const remixNodeTypes = creationContextParams?.dynamicUiSource
					? getTopLevelNodeTypes(
							creationContextParams.dynamicUiSource.fullAdf,
							creationContextParams.dynamicUiSource.startLocalId ?? '',
							creationContextParams.dynamicUiSource.endLocalId ?? '',
						)
					: undefined;
				const remixWordCount = creationContextParams?.dynamicUiSource?.fragmentAdf
					? countFragmentWords(creationContextParams.dynamicUiSource.fragmentAdf)
					: undefined;
				if (remixNodeTypes) {
					trackingAttributes.remixNodeTypes = remixNodeTypes;
				}
				if (unsupportedRemixNodeTypes) {
					trackingAttributes.unsupportedRemixNodeTypes = unsupportedRemixNodeTypes;
				}
				if (remixWordCount !== undefined) {
					trackingAttributes.remixWordCount = remixWordCount;
				}

				trackAIMAUEvent({
					type: 'initiated',
					subjectId: messageSources?.interactionSource,
					proactive: false,
					userGenerated: true,
					timestamp: streamMessage.last_message_timestamp,
					singleInstrumentationID: agentMessageId,
					deepResearchEnabled: deepResearchEnabled ?? false,
					thinkDeeperEnabled: thinkDeeperEnabled ?? false,
					fastModeEnabled: fastModeEnabled ?? false,
					webSearchEnabled: webSearchEnabled ?? false,
					pageContext: trackingAttributes.pageContext,
					viewContext: trackingAttributes.viewContext,
					invokedFrom: trackingAttributes.invokedFrom,
					attributes: {
						...getRequestedModeAttributes(trackingAttributes),
						...(additionalAnalyticAttributes && additionalAnalyticAttributes),
						...(remixNodeTypes !== undefined && { remixNodeTypes }),
						...(trackingAttributes.unsupportedRemixNodeTypes !== undefined && {
							unsupportedRemixNodeTypes: trackingAttributes.unsupportedRemixNodeTypes,
						}),
						...(remixWordCount !== undefined && { remixWordCount }),
						...(trackingAttributes.chatEntryPoint && {
							chatEntryPoint: trackingAttributes.chatEntryPoint,
						}),
					},
				});
				hasLoggedInitiatedEvent = true;
			} else if (isScopeDeterminedMessage(streamMessage)) {
				updateCurrentConversation({ scopes: streamMessage.scopes });
			} else if (isSpaceSourceRecommendationsMessage(streamMessage)) {
				// No analytics event here by design: this stream message only forwards
				// recommendations to the host via a generic callback. Surfacing/interaction
				// analytics are owned by the recommendations banner UI that consumes them.
				onSpaceSourceRecommendations?.(
					streamMessage.message.space_id,
					streamMessage.message.recommendations,
				);
			} else {
				const conversation = getState().conversations[getState().currentConversationId];
				if (conversation) {
					if (conversation.status !== CHAT_STORE_STATUS.typingResponse) {
						updateCurrentConversation({
							status: CHAT_STORE_STATUS.typingResponse,
						});
					}
				}
			}

			if (isStreamReconnectSupportPart(streamMessage)) {
				isReconnectSupported = true;
			}

			// The agents-only rollout ignores Concierge selection from Rovo Chat so it cannot leave
			// a stale Concierge input hat behind.
			const currentConversation = getState().conversations[conversationId];
			const currentConversationAgentId = currentConversation?.agent?.agent_id;
			const isAgentConversation =
				currentConversationAgentId !== undefined &&
				!isDefaultRovoChat({ agentId: currentConversationAgentId });
			const isAgentConciergeAgentsOnlyEnabled = fg('rovo_chat_agent_concierge_agents_only');
			if (
				fg('rovo_chat_agent_concierge_ui_contract') &&
				(!isAgentConciergeAgentsOnlyEnabled || isAgentConversation) &&
				isStreamConciergeAgentSelected(streamMessage)
			) {
				// A present agent_id means the concierge engaged a specialist → show the "Auto" hat.
				// An absent agent_id is a CLEAR signal, but there are two very different reasons for it:
				//   1. Genuine handback to the conversation's base agent → clear the stored Concierge
				//      agent so no stale "Auto" hat lingers.
				//   2. The user @-mentioned a specialist this turn, so concierge deliberately stood down
				//      (backend never ran evaluate(); its active specialist is only on hold). In this case
				//      we must PRESERVE the stored concierge agent so that when the user dismisses the
				//      mention hat, the "Auto" hat resumes — mirroring the backend's hold/resume behaviour.
				// In agents-only mode, only a request agent different from the conversation's base agent
				// is a mention override. Gate-off keeps the legacy non-default-agent behavior.
				const conciergeUpdate = resolveConciergeSelectedAgentUpdate({
					agentsOnlyEnabled: isAgentConciergeAgentsOnlyEnabled,
					baseConversationAgentId: currentConversationAgentId,
					requestAgentId: agentId,
					selection: streamMessage.message,
				});
				if (conciergeUpdate.type === 'set') {
					updateCurrentConversation({
						conciergeSelectedAgent: conciergeUpdate.agent,
					});
					sendAnalyticsEvent({
						eventType: 'track',
						action: 'selected',
						actionSubject: 'agentConciergeRouting',
						attributes: {
							isAgentsOnlyModeEnabled: isAgentConciergeAgentsOnlyEnabled,
						},
					});
				} else if (conciergeUpdate.type === 'clear') {
					// The base conversation agent needs no Auto hat. An absent agent remains a
					// genuine handback unless a user mention temporarily suspended Concierge.
					updateCurrentConversation({ conciergeSelectedAgent: undefined });
					sendAnalyticsEvent({
						eventType: 'track',
						action: 'handedBack',
						actionSubject: 'agentConciergeRouting',
						attributes: {
							isAgentsOnlyModeEnabled: isAgentConciergeAgentsOnlyEnabled,
						},
					});
				}
			}

			{
				const { deepResearchInvoked, twgTrace } = processResearchStreamMessage({
					streamMessage,
					tmpAssistantMessageId,
					conversationId,
					deepResearchEnabled,
					getMessages,
					setMessages,
					processStreamResearch,
					addImplicitResearchPlan,
					resetHasReceivedAnswerPart,
					researchState,
				});
				if (deepResearchInvoked) {
					trackingAttributes.deepResearchInvoked = true;
				}
				if (twgTrace) {
					trackingAttributes.twgTraceStepIds ??= [];
					trackingAttributes.twgTraceRoundIds ??= [];
					trackingAttributes.twgTraceToolNames ??= [];
					if (!trackingAttributes.twgTraceStepIds.includes(twgTrace.stepId)) {
						trackingAttributes.twgTraceStepIds.push(twgTrace.stepId);
						trackingAttributes.twgTraceRoundIds.push(twgTrace.roundId);
						if (twgTrace.toolName) {
							trackingAttributes.twgTraceToolNames.push(twgTrace.toolName);
						}
						if (twgTrace.renderMode) {
							trackingAttributes.twgTraceRenderModes ??= [];
							trackingAttributes.twgTraceRenderModes.push(twgTrace.renderMode);
						}
					}
				}
			}

			// The backend autonomously triggered thinking mode (implicit think-deeper) even though
			// the user did not explicitly enable it. Cancel the auto-mode browser metrics so these
			// slow requests don't count against the <16s/<30s SLOs, and re-attribute them to the
			// think-deeper metrics instead.
			if (
				!thinkDeeperEnabled &&
				!hasAbortedForImplicitThinkDeeper &&
				isStreamThinkDeeperTriggered(streamMessage)
			) {
				hasAbortedForImplicitThinkDeeper = true;
				// Cancel auto-mode metrics so they are not recorded in the SLOs
				stopInteractionMetricsTimeToFirstAnswerPart('cancel');
				sendMessageLatencyMetrics.cancel();
				// Re-attribute to think-deeper metrics (100s SLO target)
				implicitThinkDeeperSendMessageMetrics = messageLatencyMetrics.createSendMessageMetrics({
					thinkDeeperEnabled: true,
				});
				implicitThinkDeeperTimeToFirstAnswerPartMetrics =
					messageLatencyMetrics.createTimeToFirstAnswerPartMetrics({
						thinkDeeperEnabled: true,
					});
				implicitThinkDeeperSendMessageMetrics.start({ startTime: metricsStartTime });
				implicitThinkDeeperTimeToFirstAnswerPartMetrics.start({ startTime: metricsStartTime });
				// Abort the auto-mode reliability SLO experience and start the think-deeper one
				// so implicit think-deeper requests don't count against the auto-mode reliability SLO.
				implicitThinkDeeperExperienceName = sendMessageExperienceName({
					thinkDeeperEnabled: true,
				});
				experienceTracker.abort({
					name: sendMessageExperienceNameCompiled,
					reason: 'Re-attributed to think-deeper reliability SLO',
				});
				experienceTracker.start({
					name: implicitThinkDeeperExperienceName,
					id: conversationId,
					startTime: metricsStartTime,
					attributes: {
						conversationId,
						interactionSource: trackingAttributes.interactionSource,
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
					},
				});
			}

			// This is the first moment we have the network request id for E2E event tracking
			// Store requestId on human message for E2E event tracking
			if (!trackingAttributes.requestId && 'metadata' in streamMessage) {
				trackingAttributes.requestId = streamMessage.metadata?.request_id ?? undefined;
			}

			if (
				processAutoActionApprovalStreamMessage({
					streamMessage,
					conversationId,
					existingTraces: getConversationTraces(getState(), conversationId),
					updateTraces: (traces) => updateCurrentConversation({ traces }),
					sendAnalyticsEvent,
				})
			) {
				continue;
			}

			if (isStreamResponse(streamMessage)) {
				trackingAttributes.interactionModeExecuted =
					getInteractionModeExecutedFromStreamResponse(streamMessage);
			}

			// Handle MEMORY_UPDATE trace messages - create a persistent StoreMemoryUpdate message
			if (
				isStreamMemoryUpdate(streamMessage) &&
				// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- This Statsig experiment is not registered in platform feature flags/editor experiments.
				FeatureGates.getExperimentValue('memory_management_ux', 'isEnabled', false)
			) {
				const originalContent = streamMessage.message.content;
				const isMemoryTraceContentEmpty = !originalContent;
				let memoryContent = originalContent;
				let isMemoryContentAfterParsingEmpty = false;

				// Parse memory content: remove "Update memory:" prefix and trim
				if (memoryContent) {
					memoryContent = memoryContent.replace(/^update\s+memory:\s*/i, '').trim();
					isMemoryContentAfterParsingEmpty = !memoryContent;
				}

				const memoryUpdateMessage = {
					type: 'MEMORY_UPDATE' as const,
					content: memoryContent || undefined, // Use undefined instead of empty string
				};

				// Track memory update parsing events
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'parsed',
					actionSubject: 'memoryUpdate',
					attributes: {
						isMemoryTraceContentEmpty,
						isMemoryContentAfterParsingEmpty,
						conversationId,
					},
				});

				setMessages(
					conversationId,
					addMemorytoLatestAssistantMessage({
						chat: getMessages(conversationId),
						answerId: tmpAssistantMessageId,
						memory: memoryUpdateMessage,
						useIndex: answerIndex,
					}),
				);
			}

			// Usually the second stream part, before anything else occurs
			if (isStreamTrace(streamMessage)) {
				if (!deepResearchEnabled) {
					// we do not want traces that are not marked for deep research to be accumulated when starting research,
					// the ones that are marked are processed above in processResearchStreamMessage
					updateCurrentConversation({
						traces: dedupeTraceMessagesWithLatest([
							...getConversationTraces(getState(), conversationId),
							getTraceFromStreamMessage(streamMessage),
						]),
					});
				}

				promptStreamHook?.onTraceMessage?.(streamMessage, tmpAssistantMessageId, openChat);

				setMessages(
					conversationId,
					addRequestIdToLatestHumanMessage(
						getMessages(conversationId),
						trackingAttributes.requestId,
					),
				);
			}

			// Handle Answer Now fast mode traces for extended thinking
			// Answer Now feature - handle fast mode traces
			if (isStreamFastModeResponseAvailable(streamMessage) && agentMessageId) {
				// Fast mode response is available - show Answer Now button
				// Store both IDs in AVAILABLE status:
				// - tmpMessageId: for store operations (finding/updating the message)
				// - remoteMessageId: for the Answer Now API call
				setMessages(
					conversationId,
					updateStoreAssistantMessageAnswerNowStatus(
						getMessages(conversationId),
						tmpAssistantMessageId,
						{
							type: AnswerNowStatusType.AVAILABLE,
							tmpMessageId: tmpAssistantMessageId,
							remoteMessageId: agentMessageId,
						},
					),
				);
			}

			if (isStreamFastModeEnabled(streamMessage)) {
				// Fast mode has been enabled:
				// 1. Set status to SWITCHED (hides button)
				// 2. Clear research steps for this message
				setMessages(
					conversationId,
					updateStoreAssistantMessageAnswerNowStatus(
						getMessages(conversationId),
						tmpAssistantMessageId,
						{ type: AnswerNowStatusType.SWITCHED },
					),
				);
				// Clear research steps when switching to fast mode
				clearMessageResearch({ messageId: tmpAssistantMessageId });
			}

			if (isStreamRemoveAnswerNow(streamMessage)) {
				clearAnswerNowStatusForMessageIfSet(
					conversationId,
					tmpAssistantMessageId,
					getMessages,
					setMessages,
				);
			}

			// isStreamResearchPlan is handled by processResearchStreamMessage (called earlier in this loop)

			const extraMonitoringAttributes = streamTracking?.process(streamMessage);
			const aifcActionResult = processAifcActionStreamMessage({
				extraMonitoringAttributes,
				editRequestId,
				streamMessage,
				conversationId,
				tmpAssistantMessageId,
				traceId,
				productKey,
				deepResearchEnabled,
				storeUserMessages,
				streamActionInfo,
				trackingAttributes,
				experienceTracker,
				chatContextStoreActions,
				getMessages,
				setMessages,
				sendAnalyticsEvent,
				getIsChatMounted,
				processMessageStreamAction,
				processMessageStreamComplete,
				processMessageStreamActionClassification,
				promptStreamHook,
				openChat,
				messageSources,
				creationContextParams,
				agentId,
				agentName,
				agentIdentityAccountId,
				agentNamedId,
				isThirdPartyAgent,
				stopInteractionMetricsTimeToFirstActionPart,
			});
			if (aifcActionResult.hasReceivedActionClassification) {
				actionClassificationCount += 1;
			}
			if (aifcActionResult.hasReceivedActionComplete) {
				actionCompleteCount += 1;
			}
			isBackendCreationRequest ||= aifcActionResult.isBackendCreationAction;

			if (isStreamAnswerPart(streamMessage)) {
				const isEmptyAnswerPart =
					typeof streamMessage.message.content === 'string' &&
					streamMessage.message.content.length === 0;
				onAnswerActivity({ answerType: 'stream-answer-part', hasContent: !isEmptyAnswerPart });

				let processedStreamMessage = streamMessage;
				let hasMarkdownData = false;

				const result = getProcessStreamMarkdownMultiTag()(processedStreamMessage);
				processedStreamMessage = result.processedStreamMessage;
				const markdownData = result.markdownData;
				// LEGACY-INLINE-LUMINA
				answerUiParts?.observeParserMarkdownData(markdownData);
				if (Object.keys(markdownData).length > 0) {
					hasMarkdownData = true;
					setMessages(
						conversationId,
						addAnswerPartWithMarkdownDataMap({
							chat: getMessages(conversationId),
							answerId: tmpAssistantMessageId,
							answerPart: processedStreamMessage,
							markdownData,
							useIndex: answerIndex,
						}),
					);
				}

				promptStreamHook?.onAnswerPart?.(
					processedStreamMessage,
					tmpAssistantMessageId,
					conversationId,
					openChat,
				);
				if (!hasMarkdownData) {
					setMessages(
						conversationId,
						addAnswerPart({
							chat: getMessages(conversationId),
							answerId: tmpAssistantMessageId,
							answerPart: processedStreamMessage,
							useIndex: answerIndex,
						}),
					);
				}

				// hasAccumulatedAnswerContent tracking is handled by processResearchStreamMessage
			}

			if (answerUiParts?.handleStreamMessage(streamMessage)) {
				deadAirTracker?.recordAnswerUiUpdate(streamMessage);
			}

			if (isStreamEntityAnnotations(streamMessage) && entityAnnotationAnalyticsEnabled) {
				const entityAnnotationBatch = normalizeEntityAnnotationsMessage(streamMessage.message);
				const enabledEntities = entityAnnotationBatch.entities.filter(
					(entity) =>
						(entity.type === 'Person' && peopleEntityHoverCardsEnabled) ||
						(entity.type === 'InferredProject' && inferredProjectHoverCardsEnabled),
				);
				if (enabledEntities.length > 0) {
					entityAnnotationCounts = addEntityAnnotationBatchCounts(entityAnnotationCounts, {
						...entityAnnotationBatch,
						entities: enabledEntities,
					});
				}
				setMessages(
					conversationId,
					appendEntityAnnotationBatch({
						chat: getMessages(conversationId),
						answerId: tmpAssistantMessageId,
						entityAnnotationBatch,
					}),
				);
			}

			/**********************************************************
			 *
			 * FINAL_RESPONSE processing
			 *
			 **********************************************************/
			if (isStreamResponse(streamMessage)) {
				hasReceivedFinalResponse = true;
				// The turn ended. If it returned an action still awaiting the user's confirmation
				// (e.g. rename-page's "Confirm / Cancel"), the run needs input; otherwise it is done.
				notifyAgentRunStateForConversation(
					conversationId,
					isFinalResponseAwaitingConfirmation(streamMessage.message?.message?.actions)
						? 'needs-input'
						: 'done',
				);
				const shouldUseBackendCreationExperienceLifecycle = isBackendCreationRequest;
				// No completed actions falls through to abort this experience + others.
				if (shouldUseBackendCreationExperienceLifecycle && actionCompleteCount > 0) {
					// Backend creation responses do not require opening a preview to reach a terminal state.
					completeAifcActionExperience(
						experienceTracker,
						ExperienceName.AIFC_ALL_DATA_HANDLED,
						actionClassificationCount,
						actionCompleteCount,
						isBackendCreationRequest,
					);
				}
				// Always succeed AIFC_MESSAGE from FINAL_RESPONSE for backend multi-create events, or when
				// the parity experiment is enabled to streamline frontend and backend handling.
				if (
					actionCompleteCount > 0 &&
					(shouldUseBackendCreationExperienceLifecycle ||
						isExperimentEnabled('cwr-fe-be-experience-analytics-streamline'))
				) {
					completeAifcActionExperience(
						experienceTracker,
						ExperienceName.AIFC_MESSAGE,
						actionClassificationCount,
						actionCompleteCount,
						isBackendCreationRequest,
					);
				}
				const humanMessage = streamMessage?.message?.human_message;
				const humanMessageId = humanMessage?.id?.toString();
				const finalResponseAgentMessageId = streamMessage?.message?.message?.id.toString();
				agentMessageId = finalResponseAgentMessageId;
				// Record the backend-assigned conversation ID on the optimistic entry so the
				// space sidenav merge logic can dedupe between the local optimistic entry
				// (client UUID) and the API-returned entry (backend ID), and display the
				// correct name from the chat store rather than the timestamp fallback.
				const backendConversationId = streamMessage?.message?.message?.conversation_channel_id;
				if (backendConversationId) {
					updateCurrentConversation({ backendConversationId });
				}
				const minionOrchestrationOutputs = streamMessage?.message?.message?.minion_outputs;
				// Only clear traces if they are not already empty to avoid unnecessary state updates
				clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);
				// INVARIANT: updateResearchMessageId MUST be called before replaceAnswerData (and the
				// setMessages call that wraps it). Both stores must agree on the same messageId key
				// before React re-renders — if the chat store ID flips to finalResponseAgentMessageId first,
				// the next render will call getMessageResearchById(finalResponseAgentMessageId) and get undefined
				// because the research store is still keyed under tmpAssistantMessageId, causing
				// completed traces to disappear.
				//
				// Nothing between this call and replaceAnswerData below should read the research
				// store using tmpAssistantMessageId (markdown processing and clearTracesIfNotEmpty
				// above do not touch the research store after this point).
				if (tmpAssistantMessageId !== finalResponseAgentMessageId) {
					updateResearchMessageId({
						messageId: tmpAssistantMessageId,
						newMessageId: finalResponseAgentMessageId,
					});
				}

				const { processedStreamMessage, markdownData } = processStreamResponseMarkdown({
					streamMessage,
					messageId: finalResponseAgentMessageId,
					conversationId,
					sendAnalyticsEvent,
					shouldPreprocessDetails: shouldPreprocessDetailsForConversation(
						getState(),
						conversationId,
					),
				});

				// Capture whether the final answer contained inline Lumina blocks so it
				// can be reported on the `agentMessage received` event. The markdown
				// extractor stores those blocks under `lumina-*` keys in `markdownData`.
				// Joined (on messageId) against the `luminaComponent shown` render
				// event to detect Lumina that was returned by BE but never rendered.
				const inlineLuminaCount = Object.keys(markdownData).filter((key) =>
					key.startsWith('lumina-'),
				).length;
				if (inlineLuminaCount > 0) {
					trackingAttributes.hasLumina = true;
					trackingAttributes.luminaType = 'inline';
					trackingAttributes.luminaCount = inlineLuminaCount;
				}

				if (entityAnnotationAnalyticsEnabled) {
					trackingAttributes.personEntityAnnotationCount =
						entityAnnotationCounts.personEntityAnnotationCount;
					trackingAttributes.inferredProjectEntityAnnotationCount =
						entityAnnotationCounts.inferredProjectEntityAnnotationCount;
				}

				// Finish view dead air tracking before rendering final response
				// This ensures the last tracked interval is up to FINAL_RESPONSE arrival,
				// and any DOM updates from replaceAnswerData are not tracked
				viewDeadAirBroker?.finish('completed');

				promptStreamHook?.onFinalResponse?.(processedStreamMessage, tmpAssistantMessageId);
				setMessages(
					conversationId,
					replaceAnswerData({
						chat: getMessages(conversationId),
						answerId: tmpAssistantMessageId,
						content: processedStreamMessage,
						useIndex: answerIndex,
						humanMessageId,
						markdownData: Object.keys(markdownData).length > 0 ? markdownData : undefined,
						traceId,
						onMessageIdChanged: onTempMessageIdReplacedOverride || containerOnTempMessageIdReplaced,
					}),
				);
				if (entityAnnotationAnalyticsEnabled && finalResponseAgentMessageId) {
					// Late annotation batches must target the ID stored by FINAL_RESPONSE.
					tmpAssistantMessageId = finalResponseAgentMessageId;
				}
				answerUiParts?.onFinalResponse();

				if (humanMessageId) {
					setMessages(
						conversationId,
						updateHumanMessageId({
							chat: getMessages(conversationId),
							oldId: tmpHumanMessageId,
							newId: humanMessageId,
							parentMessageId: humanMessage?.parent_message_id,
							onMessageIdChanged: (oldId, newId) =>
								containerOnHumanMessageIdReplaced?.(conversationId, oldId, newId),
						}),
					);
				}

				const allMessages = getMessages(conversationId);
				const finalMessage = processedStreamMessage.message.message;
				if (finalMessage.actions?.length) {
					canCompleteRemixWithText = false;
				}
				isValidRemixTextResponse =
					canCompleteRemixWithText &&
					!abortController.signal.aborted &&
					!guardEventReceived &&
					typeof finalMessage.content === 'string' &&
					finalMessage.content.trim().length > 0;
				const isValidAction =
					isValidRemixTextResponse ||
					handleStagingAreaActionValidation(
						allMessages,
						conversationId,
						traceId,
						chatContextStoreActions,
						experienceTracker,
						getMessages,
						setMessages,
						sendMessageLatencyMetrics,
						cancelStreamingMetrics,
						{ llmDebugLogsEnabled: storeUserMessages },
						productKey,
						minionOrchestrationOutputs,
						creationContextParams?.experience,
					);

				if (!isValidAction) {
					return {
						hasReceivedFinalResponse,
						hasReceivedPromptRedaction,
						hasLoggedInitiatedEvent,
						guardEventReceived,
						recoveredAfterStreamTimeout,
						assistantMessageId: finalResponseAgentMessageId,
					};
				}

				const finalActions = processedStreamMessage.message.message.actions || [];
				if (
					productKey === 'confluence' &&
					(creationContextParams?.experience === 'inline_edit' ||
						creationContextParams?.experience === 'chat_edit') &&
					fg('rovo-chat-ai-inbox') &&
					isAiXstateMigrationEnabled() &&
					!abortController.signal.aborted
				) {
					// Final response is the acknowledgement signal; editor application may still be pending.
					for (const action of finalActions) {
						if (
							action.key !== 'aifc-adf-update' ||
							action.status !== 'planned' ||
							acknowledgedInlineEdits.has(action.invocationId)
						) {
							continue;
						}
						acknowledgedInlineEdits.add(action.invocationId);
						// Temporary mitigation (ROVOFE-4126): FINAL_RESPONSE can arrive before BE
						// persists the action, causing a 404 and a failure message in history.
						// Allow 5s for persistence; remove once BE guarantees save-before-response.
						// This reduces the race but cannot guarantee persistence has finished.
						setTimeout(() => {
							if (abortController.signal.aborted) {
								return;
							}
							void assistanceService
								.resolveConversationAction(conversationId, {
									context: {
										...action,
										messageId: finalResponseAgentMessageId,
										resolution: { type: 'actioned' },
									},
								})
								.catch((error) => {
									logException({ exception: error, name: 'InlineEditActionResolutionError' });
								});
						}, 5000);
					}
				}
				if (finalActions.length > 0) {
					const firstActionData = finalActions[0]?.data as
						| { dynamicUiSubtype?: string }
						| undefined;
					stopInteractionMetricsTimeToActionCompletion('stop', {
						conversationId,
						actionType: 'final-actions',
						actionCount: finalActions.length,
						actionKeys: finalActions.map((a) => a.key),
						actionPartKey: getActionPartKeyForMetrics(
							streamActionInfo.actionKey,
							creationContextParams?.source,
							firstActionData?.dynamicUiSubtype,
						),
						interactionSource: messageSources?.interactionSource,
						remixType: creationContextParams?.dynamicUiType,
					});
				} else {
					stopInteractionMetricsTimeToActionCompletion('cancel');
				}

				// Update tracking attributes from FINAL_RESPONSE using the shared extractor.
				const streamMeta = extractStreamMeta(
					streamMessage as StreamResponse,
					skillSelectionSource,
					expValEquals('cc-maui-experiment', 'isEnabled', true),
				);

				if (!hasLoggedInitiatedEvent) {
					trackingAttributes.messageId = finalResponseAgentMessageId;
					trackingAttributes.instrumentationId = finalResponseAgentMessageId;
				}
				trackingAttributes.humanMessageId = streamMeta.humanMessageId;
				trackingAttributes.hasSources = streamMeta.hasSources;
				trackingAttributes.hasAuthoritativeSources = streamMeta.hasAuthoritativeSources;
				// Only carry the detailed authority fields when authoritative sources are
				// present, matching the guarded spread in reconnectToInProgressStreamProvider
				// so the gate-off case does not populate trackingAttributes with undefined fields.
				if (streamMeta.hasAuthoritativeSources) {
					trackingAttributes.authorityLevel = streamMeta.authorityLevel;
					trackingAttributes.isHighlyAuthoritative = streamMeta.isHighlyAuthoritative;
					trackingAttributes.overallConfidence = streamMeta.overallConfidence;
					trackingAttributes.authoritativeSourceCount = streamMeta.authoritativeSourceCount;
					trackingAttributes.authoritativeExpertCount = streamMeta.authoritativeExpertCount;
				}
				trackingAttributes.citedProductList = streamMeta.citedProductList;
				trackingAttributes.connectorInfo = streamMeta.connectorInfo;
				trackingAttributes.sourceProducts = streamMeta.sourceProducts;
				trackingAttributes.peopleDataProviders = streamMeta.peopleDataProviders;
				if (!shouldUseBackendCreationExperienceLifecycle) {
					trackingAttributes.messageActions = streamMeta.messageActions;
				}
				trackingAttributes.messageDynamicUIData = streamMeta.messageDynamicUIData;
				trackingAttributes.visualBlockCounts = streamMeta.visualBlockCounts;
				trackingAttributes.actionObjectId = streamMeta.actionObjectId;
				trackingAttributes.invokedFrom =
					streamMeta.invokedFrom ?? messageSources?.invokedFrom ?? trackingAttributes.invokedFrom;
				trackingAttributes.scenarioId = streamMeta.scenarioId;
				trackingAttributes.sources = streamMeta.responseSources;
				trackingAttributes.usesJsm = streamMeta.usesJsm;
				// Keep Set type for compatibility with downstream consumers that call Array.from()
				trackingAttributes.pluginsInvoked = new Set(streamMeta.pluginsInvoked);
				trackingAttributes.skillsInvoked = streamMeta.skillsInvoked;
				trackingAttributes.executedStratusSkill = streamMeta.executedStratusSkill;
				trackingAttributes.agentExternalConfigReference = streamMeta.agentExternalConfigReference;

				const flatMinionOutputs = minionOrchestrationOutputs?.flat() ?? [];

				// Analytics: Verify if minion alias matches minion id
				minionVerificationAnalytics({
					minionAlias,
					...(requestedSkills?.length ? { requestedSkills } : {}),
					agentId,
					minionsOutput: flatMinionOutputs,
					sendAnalyticsEvent,
				});
				// Receipt alone is insufficient: hooks, validation, and store writes above
				// must finish before exhausted reconnect recovery can retain this answer.
				hasProcessedFinalResponse = true;
			}

			processInteractionConclusion({
				streamObj,
				streamMessage,
				conversationId,
				answerIndex,
				requestId: trackingAttributes.requestId,
				getMessages,
				setMessages,
				sendAnalyticsEvent,
			});

			if (isStreamAgenticUi(streamObj)) {
				const normalizedAgenticUiMessage: StreamAgenticUi = {
					...streamObj.data,
					type: 'AGENTIC_UI',
				};
				const incomingA2UIMessages = normalizedAgenticUiMessage.message.content;
				if (Array.isArray(incomingA2UIMessages)) {
					turnSurfaces ??= findAddressableSurfaces(getMessages(conversationId));
					setMessages(
						conversationId,
						routeA2UIMessages({
							messages: getMessages(conversationId),
							incomingA2UIMessages: incomingA2UIMessages as A2uiSurfaceAdapterRovoMessage[],
							createFallbackMessageId: tmpAssistantMessageId,
							addressable: turnSurfaces,
						}),
					);
				}
			}

			if (isFollowUpMessage(streamMessage)) {
				hasReceivedFollowUp = true;
				const messages = getMessages(conversationId);
				const originatingAssistant = [...messages].reverse().find(isStoreAssistant);
				const originatingAgentIdentity =
					isExperimentEnabled('rovo_chat_mention_agents') && originatingAssistant
						? deriveFollowUpSendAgentIdentity(originatingAssistant)
						: undefined;
				// Only clear traces if they are not already empty to avoid unnecessary state updates
				clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);

				const augmentedFollowUp = appendCreatePageFollowUpIfNeeded(
					messages,
					streamMessage,
					productKey,
					chatContextStoreActions.getIsStagingArea(),
				);

				setMessages(conversationId, [
					...messages,
					{
						type: 'FOLLOW_UP',
						content: augmentedFollowUp,
						id: randomIntFromInterval(0, 1000000),
						agentMessageId: getLastAgentMessageID(messages),
						...(originatingAgentIdentity ? { originatingAgentIdentity } : {}),
						humanMessageId: trackingAttributes.humanMessageId,
					},
				]);
			}

			/**
			 * Task status processing
			 */
			if (isStreamTaskStatus(streamMessage)) {
				onTaskStatusUpdate?.(streamMessage);
			}

			/**
			 * Todo snapshot processing
			 */
			if (isStreamTodoSnapshot(streamMessage)) {
				onTodoSnapshot?.({ todos: streamMessage.message.todos });
			}

			if (isStreamActionError(streamMessage)) {
				slidesAiEditLatency.markFailed(editRequestId);
				// Producers disagree on where the error text lives, so telemetry probes each known
				// location. Displayed copy keeps the original source so users see the same string.
				const displayedErrorMessage =
					streamMessage.message?.data?.message || ACTION_EXECUTION_FAILED;
				const resolvedErrorMessage =
					streamMessage.message?.data?.message ||
					streamMessage.message?.data?.content ||
					streamMessage.message?.content ||
					ACTION_EXECUTION_FAILED;
				const actionError = new StreamErrorException(displayedErrorMessage, {
					detail: {
						message: {
							content: resolvedErrorMessage,
							message_template: streamMessage?.message?.key,
						},
						metadata: {
							...streamMessage.metadata,
							isStagingArea: chatContextStoreActions.getIsStagingArea(),
							productKey,
						},
					},
					traceId: traceId,
				});

				// Opening chat on error can abort the inline stream
				if (
					isCurrentRemixRegenerationStream &&
					!abortController.signal.aborted &&
					!guardEventReceived
				) {
					experienceTracker.fail({
						name: ExperienceName.REMIX_REGENERATE,
						error: actionError,
						attributes: { conversationId, actionKey: streamMessage.message.key, traceId },
					});
				}

				sendMessageLatencyMetrics.cancel();
				cancelStreamingMetrics();
				processMessageStreamError?.({
					actionKey: streamActionInfo.actionKey,
					errorType: streamMessage?.message?.key,
					errorMessage: resolvedErrorMessage,
					traceId,
				});
				promptStreamHook?.onError?.(streamMessage, tmpAssistantMessageId, {
					openChatOnError,
					conversationId,
					traceId,
				});
				broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

				experienceTracker.fail({
					name: sendMessageExperienceNameCompiled,
					error: actionError,
					attributes: {
						conversationId,
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
						isDeepResearchEnabled: deepResearchEnabled,
						isThinkDeeperEnabled: thinkDeeperEnabled,
						isFastModeEnabled: fastModeEnabled,
						isWebSearchEnabled: webSearchEnabled,
						llmDebugLogsEnabled: storeUserMessages,
						productKey,
						isStreamAction: streamActionInfo.isAction,
						actionKey: streamActionInfo.actionKey,
						traceId,
						...remixRegenerateRequestAttributes,
					},
				});

				experienceTracker.fail({
					name: ExperienceName.AIFC_MESSAGE,
					error: actionError,
					attributes: {
						productKey,
						isStreamAction: streamActionInfo.isAction,
						actionKey: streamActionInfo.actionKey,
						llmDebugLogsEnabled: storeUserMessages,
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
						latestStreamMessageMillisOffset:
							latestStreamMessage && 'millisOffset' in latestStreamMessage
								? latestStreamMessage.millisOffset
								: undefined,
						latestStreamMessageType: latestStreamMessage?.type,
					},
				});

				const reason = streamActionInfo.isAction
					? 'action_error_while_executing_action'
					: 'action_error_without_classification';
				for (const name of [
					ExperienceName.AIFC_INITIAL_DATA_RECEIVED,
					ExperienceName.AIFC_ALL_DATA_HANDLED,
				]) {
					experienceTracker.fail({
						name,
						error: actionError,
						attributes: {
							productKey,
							actionKey: streamActionInfo.actionKey,
							reason,
						},
					});
				}

				throw actionError;
			}

			if (isStreamPromptRedaction(streamMessage)) {
				// History eligibility must not depend on Guard UI controls.
				hasReceivedPromptRedaction = true;
				const redactingTypes =
					(
						FeatureGates.getExperiment('rovo_chat_redacting_guard_config').value as {
							value?: string[];
						}
					)?.value ?? [];
				if (
					redactingTypes.includes(streamMessage.type) &&
					fg('rovo-guard-ui-redaction-and-blocking')
				) {
					// Do NOT set guardEventReceived — the stream continues with the actual response after redaction.
					onPromptRedacted?.(streamMessage);
				}
			}

			if (isStreamReasoningPart(streamMessage)) {
				notifyAgentRunStateForConversation(conversationId, 'thinking');
			}

			if (isStreamPromptBlock(streamMessage)) {
				const blockingTypes =
					(
						FeatureGates.getExperiment('rovo_chat_blocking_guard_config').value as {
							value?: string[];
						}
					)?.value ?? [];
				if (
					blockingTypes.includes(streamMessage.type) &&
					fg('rovo-guard-ui-redaction-and-blocking')
				) {
					guardEventReceived = true;
					onPromptBlocked?.(streamMessage);
				}
			}

			if (isStreamResponseRedaction(streamMessage)) {
				const redactingTypes =
					(
						FeatureGates.getExperiment('rovo_chat_redacting_guard_config').value as {
							value?: string[];
						}
					)?.value ?? [];
				if (
					redactingTypes.includes(streamMessage.type) &&
					fg('rovo-guard-ui-redaction-and-blocking')
				) {
					// Do NOT set guardEventReceived — stream continues after response redaction.
					// Clear the unredacted answer + traces and stamp redactedResponse inline here,
					// in the same batch, so no stale getMessages read can re-expose the content
					// (PRGD-488).
					applyResponseRedactionToMessage({
						conversationId,
						messageId: agentMessageId ?? tmpAssistantMessageId,
						redactedResponse: streamMessage.message.redacted_response,
						getMessages,
						setMessages,
						getState,
						updateCurrentConversation,
					});
					// Clear the unredacted research-round content and settle the steps. Keyed on the
					// FINAL_RESPONSE message id (agentMessageId): after FINAL_RESPONSE the research
					// store is re-keyed to the server id via updateResearchMessageId.
					processStreamResearch({
						messageId: agentMessageId ?? tmpAssistantMessageId,
						streamMessage,
						isDeepResearch: deepResearchEnabled ?? false,
					});
					onResponseRedacted?.(streamMessage);
				}
			}

			if (isStreamResponseBlock(streamMessage)) {
				const blockingTypes =
					(
						FeatureGates.getExperiment('rovo_chat_blocking_guard_config').value as {
							value?: string[];
						}
					)?.value ?? [];
				if (
					blockingTypes.includes(streamMessage.type) &&
					fg('rovo-guard-ui-redaction-and-blocking')
				) {
					guardEventReceived = true;
					onResponseBlocked?.(streamMessage);
				}
			}

			if (isStreamAttachmentBlock(streamMessage)) {
				const blockingTypes =
					(
						FeatureGates.getExperiment('rovo_chat_blocking_guard_config').value as {
							value?: string[];
						}
					)?.value ?? [];
				if (
					blockingTypes.includes(streamMessage.type) &&
					fg('rovo-guard-ui-redaction-and-blocking')
				) {
					guardEventReceived = true;
					onAttachmentBlocked?.(streamMessage);
				}
			}

			if (isStreamError(streamMessage)) {
				processMessageStreamError?.({
					actionKey: streamActionInfo.actionKey,
					errorType: streamMessage?.message?.message_template,
					errorMessage: streamMessage?.message?.content,
					traceId,
				});
				// If a guard event (PROMPT_REDACTION, PROMPT_BLOCKED, RESPONSE_BLOCKED, or ATTACHMENT_BLOCKED) was
				// already received, the backend always follows with an ERROR to terminate the stream.
				// Treat it as a normal end of the guard flow — break without throwing.
				if (guardEventReceived) {
					// A guard block (prompt/response/attachment) is terminated by this backend error;
					// surface it once here as the run's blocked state.
					notifyAgentRunStateForConversation(conversationId, 'blocked');
					break;
				}

				const { message, metadata, millisOffset, type } = streamMessage;

				// AUP violations are still considered successful
				if (isStreamAUPError(streamMessage)) {
					experienceTracker.succeed({
						name: sendMessageExperienceNameCompiled,
					});
					experienceTracker.succeed({ name: ExperienceName.AIFC_MESSAGE });

					// [MAUI-1070] In the remix flow, treat an AUP violation as non-fatal:
					// keep reading so the friendly response (streamed right after the guard)
					// arrives instead of being discarded by the throw below. `onSafetyViolation`
					// lets the editor-remix UI surface it (dismiss thinking bar, open chat).
					// Scoped to `source === 'remix'`; other flows keep the existing throw.
					if (creationContextParams?.source === 'remix') {
						promptStreamHook?.onSafetyViolation?.(conversationId);
						continue;
					}
				}

				sendMessageLatencyMetrics.cancel();
				deadAirTracker?.cancel();
				viewDeadAirBroker?.finish('errored');
				cancelStreamingMetrics();

				promptStreamHook?.onError?.(streamMessage, tmpAssistantMessageId, {
					openChatOnError,
					conversationId,
					traceId,
				});
				broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

				// A genuine error that actually terminates the run. Placed after the AUP remix path
				// above, which continues rather than throwing, so that path is not marked failed.
				notifyAgentRunStateForConversation(conversationId, 'failed');

				throw new StreamErrorException(message?.content || 'Unknown', {
					detail: {
						message,
						metadata,
						millisOffset,
						type,
					},
					traceId: traceId,
				});
			}
		}

		if (
			lastStreamTimeoutError &&
			!hasReceivedFinalResponse &&
			!guardEventReceived &&
			!(await recoverAfterStreamTimeout(lastStreamTimeoutError))
		) {
			throw lastStreamTimeoutError;
		}
	} catch (error: unknown) {
		slidesAiEditLatency.end(
			editRequestId,
			abortController.signal.aborted ? 'cancelled' : 'failure',
		);
		/* for await catch block */
		const err = error instanceof Error ? error : new Error(String(error));
		streamTracking?.fail(err);

		if (err instanceof StreamErrorException) {
			throw err;
		}

		if (err instanceof RovoChatStreamTimeoutError) {
			sendMessageLatencyMetrics.cancel();
			deadAirTracker?.cancel();
			viewDeadAirBroker?.finish('aborted');
			cancelStreamingMetrics();
			broadcastStreamLifecycle(StreamLifecycleState.ERRORED);
			experienceTracker.fail({
				name: sendMessageExperienceNameCompiled,
				error: err,
				attributes: {
					conversationId,
					deepResearchEnabled,
					thinkDeeperEnabled,
					fastModeEnabled,
					webSearchEnabled,
					llmDebugLogsEnabled: storeUserMessages,
					productKey,
					isStreamAction: streamActionInfo.isAction,
					actionKey: streamActionInfo.actionKey,
					featureArea: chatContextStoreActions.getIsStagingArea() ? 'aifc-staging-area' : 'unknown',
					traceId,
					...remixRegenerateRequestAttributes,
				},
			});
			throw err;
		}

		// this is for any errors that happening during processing the stream
		// e.g. BE terminates streaming suddenly
		sendMessageLatencyMetrics.cancel();
		deadAirTracker?.cancel();
		viewDeadAirBroker?.finish('aborted');

		cancelStreamingMetrics();

		if (isClientSideStreamingError(err)) {
			const isReconnectable = !!(agentMessageId && isReconnectSupported);

			sendAnalyticsEvent({
				eventType: 'track',
				action: 'failed',
				actionSubject: 'rovoChatStream',
				attributes: {
					...streamStats.getStats(),
					isDeepResearchEnabled: deepResearchEnabled,
					isThinkDeeperEnabled: thinkDeeperEnabled,
					isFastModeEnabled: fastModeEnabled,
					isWebSearchEnabled: webSearchEnabled,
					isAIFCStreaminAction: streamActionInfo.isAction,
					isReconnectable,
					conversationId,
					instrumentationId: trackingAttributes.instrumentationId,
					requestId: trackingAttributes.requestId,
					interactionSource: trackingAttributes.interactionSource,
					contentContext: chatContextStoreActions.getIsStagingArea() ? 'staging-area' : 'global',
					userAgent: navigator?.userAgent,
					error: err.message,
				},
			});
		}

		broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

		// When reconnect attempt fails
		if (!deepResearchEnabled) {
			const reason = streamActionInfo.isAction
				? 'stream_error_during_action'
				: 'stream_error_before_classification';
			for (const name of [
				ExperienceName.AIFC_INITIAL_DATA_RECEIVED,
				ExperienceName.AIFC_ALL_DATA_HANDLED,
			]) {
				experienceTracker.fail({
					name,
					error: err,
					attributes: {
						productKey,
						actionKey: streamActionInfo.actionKey,
						reason,
					},
				});
			}
		}

		throw new StreamGeneratorError(
			err.message ?? 'Stream generator error',
			{
				traceId: traceId,
				detail: {
					millisOffset:
						latestStreamMessage && 'millisOffset' in latestStreamMessage
							? latestStreamMessage.millisOffset
							: undefined,
					type: latestStreamMessage?.type ?? '',
				},
			},
			err,
		);
	} finally {
		unsubscribeRemixTrackers?.();
		inactivity?.stop();
		deadAirTracker?.complete();
		viewDeadAirBroker?.finish('completed');
		// The only cleanup on stop, idle timeout and stream error: processStream rethrows
		// and the send path then skips markStreamingMessagesAsComplete.
		answerUiParts?.onStreamEnd();
		// Safety net for the agent-run bridge: if the turn ended without settling (abort, timeout,
		// dropped request), clear the run so its node does not stay active. No-op on the normal
		// paths, which have already reached done / needs-input / a terminal state.
		endActiveAgentRun(conversationId);
	}

	/**********************************************************
	 *
	 * Stream processing END
	 *
	 **********************************************************/

	if (actionClassificationCount > 0 && actionCompleteCount === 0 && hasReceivedFinalResponse) {
		// In the case ACTION_COMPLETE was never sent, it's likely that `AdfEditorMinion` early-exits with "no operations returned by LLM". So we abort the experiences rather than letting them timeout. This can potentially happen when LLM needs clarification.
		abortGenerateContentChildExperiences(
			experienceTracker,
			'stream_completed_without_action_complete',
			{
				actionKey: streamActionInfo.actionKey,
				productKey,
			},
		);
	}

	if (!hasReceivedFollowUp && hasReceivedFinalResponse) {
		const messages = getMessages(conversationId);
		if (
			shouldAppendCreatePageFollowUp(
				messages,
				productKey,
				chatContextStoreActions.getIsStagingArea(),
			)
		) {
			const originatingAssistant = [...messages].reverse().find(isStoreAssistant);
			const originatingAgentIdentity =
				isExperimentEnabled('rovo_chat_mention_agents') && originatingAssistant
					? deriveFollowUpSendAgentIdentity(originatingAssistant)
					: undefined;
			setMessages(conversationId, [
				...messages,
				{
					type: 'FOLLOW_UP',
					content: createSyntheticCreatePageFollowUp(),
					id: randomIntFromInterval(0, 1000000),
					agentMessageId: getLastAgentMessageID(messages),
					...(originatingAgentIdentity ? { originatingAgentIdentity } : {}),
					humanMessageId: trackingAttributes.humanMessageId,
				},
			]);
		}
	}

	updateCurrentConversation({
		sourceAgentId: getSourceAgentId(
			getMessages(conversationId),
			getConversation(getState(), conversationId),
		),
	});

	if (
		canCompleteRemixWithText &&
		isValidRemixTextResponse &&
		!abortController.signal.aborted &&
		!guardEventReceived
	) {
		experienceTracker.succeed({
			name: ExperienceName.REMIX_REGENERATE,
			attributes: { conversationId, responseType: 'text' },
		});
	}

	return {
		hasReceivedFinalResponse,
		hasReceivedPromptRedaction,
		hasLoggedInitiatedEvent,
		hasAbortedForImplicitThinkDeeper,
		implicitThinkDeeperSendMessageMetrics,
		implicitThinkDeeperExperienceName,
		guardEventReceived,
		recoveredAfterStreamTimeout,
		assistantMessageId:
			hasReceivedFinalResponse && agentMessageId ? agentMessageId : tmpAssistantMessageId,
	};
};
